import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomModal from './components/CustomModal';
import { useAuth } from '../../../auth';
import { useAppSelector } from '../../../../../redux/hooks';
import Swal from 'sweetalert2';
import { getConnectionList, getConnectionDetails, deleteConnection, getConnectionListNoTenant } from './components/services';
import { FaPlus } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

type ID = undefined | null | number;

function SSO() {
    const { currentUser } = useAuth();
    const tenant = useAppSelector(state => state.TenantSlice.id);

    // Variable para controlar la visibilidad del modal
    const [showModal, setShowModal] = useState(false);

    // Variable para almacenar el ID de la conexión
    const [ID, setID] = useState<ID>(undefined);

    // Estado para almacenar los detalles de la conexión que se edita
    const [connectionDetails, setConnectionDetails] = useState<any | null>(null);

    const navigate = useNavigate();
    const [records, setRecords] = useState<any | null>([]);

    // Estado para controlar si existe una conexión para el tenant actual
    const [hasConnection, setHasConnection] = useState(false);

    // Llamada a axios y obtención de los datos
    useEffect(() => {
        refreshConnectionList();
    }, [tenant, currentUser]);

    // Función para actualizar el listado de conexiones
    const refreshConnectionList = () => {
        if (tenant) {
            getConnectionList(tenant)
                .then((res: { data: { connection_list: any; }; }) => {
                    const filteredConnections = res.data.connection_list.filter(
                        (connection: any) => connection.tenant_id === tenant
                    );
                    setRecords(filteredConnections);
                    setHasConnection(filteredConnections.length > 0);
                })
                .catch((error: any) => {
                    console.error("Error fetching connection list:", error);
                });
        } else {
            getConnectionListNoTenant()
                .then((res: { data: { connection_list: any; }; }) => {
                    setRecords(res.data.connection_list);
                    setHasConnection(res.data.connection_list.length > 0);
                })
                .catch((error: any) => {
                    console.error("Error fetching connection list without tenant:", error);
                });
        }
    };

    // Función para abrir el modal
    const handleOpenModal = () => {
        setShowModal(true);
        setID(null);
    };

    // Función para abrir el modal con detalles para editar
    const handleEdit = (connectionId: number) => {
        setID(connectionId);
        getConnectionDetails(connectionId)
            .then((res: { data: any }) => {
                setConnectionDetails(res.data.details);
                setShowModal(true);
            })
            .catch((error: any) => {
                console.error("Error fetching connection details:", error);
            });
    };

    // Función para eliminar una conexión
    const handleDelete = (connectionId: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteConnection(connectionId)
                    .then(() => {
                        Swal.fire('Deleted!', 'Your connection has been deleted.', 'success');
                        refreshConnectionList();
                    })
                    .catch(error => {
                        console.error('Error deleting connection:', error);
                        Swal.fire('Error', 'There was an error deleting the connection.', 'error');
                    });
            }
        });
    };

    // Función para cerrar el modal
    const handleCloseModal = () => {
        setShowModal(false);
        setID(undefined);
        setConnectionDetails(null);
    };

    return (
        <>
            <div className='card'>
                {/* Header */}
                <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='f-titulo text-marino fs-md'>SSO Management</span>
                    </h3>

                    {/* Botón para abrir el modal de SSO */}
                    <div className='card-toolbar'>
                        {currentUser?.ListPermiso?.includes('User.Create') && (
                            <button
                                onClick={handleOpenModal}
                                className='btn-light-marino f-parrafo d-flex align-items-center'
                                disabled={hasConnection}
                                style={{ opacity: hasConnection ? 0.5 : 1, cursor: hasConnection ? 'not-allowed' : 'pointer' }}
                            >
                                <FaPlus style={{ marginRight: '0.5rem' }} />
                                New SSO Configuration
                            </button>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className='card-body py-3'>
                    <div className='tab-content'>
                        <div className='tab-pane fade show active' id='kt_table_widget_5_tab_1'>
                            <div className='table-responsive'>
                                <table className='w-full f-parrafo caption-bottom custom-table' id='table'>
                                    <thead className="table-header-light bg-marino">
                                        <tr>
                                            <th className='table-cell'>Name</th>
                                            <th className='table-cell'>Audience URI</th>
                                            <th className='table-cell'>Email Domain</th>
                                            <th className='table-cell'>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="text-center">
                                                    <span className='text-pink fw-bold fs-p'>No Data Available</span>
                                                </td>
                                            </tr>
                                        ) : (
                                            records.map((connection: any, index: number) => (
                                                <tr key={index} className='table-row'>
                                                    <td className='table-cell'>{connection.name}</td>
                                                    <td className='table-cell'>{connection.audience_uri || 'N/A'}</td>
                                                    <td className='table-cell'>{connection.email_domain}</td>
                                                    <td className='table-cell'>
                                                        <div className='d-flex align-items-center'>
                                                            <button
                                                                className='btn btn-marino f-parrafo'
                                                                style={{ width: '100%', marginRight: '0.75rem' }}
                                                                onClick={() => handleEdit(connection.connection_id)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                className='btn btn-danger f-parrafo'
                                                                style={{ width: '100%' }}
                                                                onClick={() => handleDelete(connection.connection_id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para la configuración de SSO/SCIM */}
            {showModal && (
                <CustomModal
                    show={showModal}
                    handleClose={handleCloseModal}
                    title={
                        <span className="d-flex align-items-center">
                            <FaPlus style={{ marginRight: '0.5rem' }} />
                            Create New SSO Configuration
                        </span>
                    }
                    portName="SSO Configuration"
                    ssoDetails={connectionDetails}
                    onSave={refreshConnectionList}
                />
            )}
        </>
    );
}

export default SSO;
